import { ApiService } from './api';

export interface Phase1Proposal {
  businessSummary: string;
  offerings: { name: string; description: string }[];
  icp: {
    title: string;
    seniority: string;
    industry: string;
    companySize: string;
    geography: string;
    customFilters: string[];
  };
  reasoning: string;
}

export interface Phase2Proposal {
  outreachGoal: string;
  messagingTone: 'professional' | 'casual' | 'consultative' | 'direct';
  sequenceSteps: {
    stepIndex: number;
    type: 'email';
    delay: number;
    delayUnit: 'days' | 'hours';
    subject: string;
    contentSketch: string;
  }[];
  reasoning: string;
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
}

export interface WizardSessionResponse {
  sessionId: string;
  currentPhase: number;
  proposal: Phase1Proposal | Phase2Proposal | null;
  phaseData?: Record<string, unknown>;
  status?: string;
  usage?: TokenUsage;
  toolActivity?: string[];
  messages?: {
    role: string;
    content: string;
    structuredData?: unknown;
    createdAt: string;
  }[];
}

export interface ConfirmResponse {
  approved: boolean;
  challenges?: string[];
  currentPhase: number;
  proposal?: Phase1Proposal | Phase2Proposal;
  phaseData?: Record<string, unknown>;
  usage?: TokenUsage;
}

export interface CompleteResponse {
  agentId: string;
  campaignId: string;
}

const api = new ApiService();

export const agentWizardService = {
  start: (data: { websiteUrl?: string; manualDescription?: string }) =>
    api.post<WizardSessionResponse>('/api/agents/wizard/start', data),

  getSession: (sessionId: string) =>
    api.get<WizardSessionResponse>(`/api/agents/wizard/${sessionId}`),

  sendIntro: (sessionId: string, prompt?: string) =>
    api.post<{ reply: string; usage?: TokenUsage }>(
      `/api/agents/wizard/${sessionId}/intro`,
      prompt ? { prompt } : undefined
    ),

  sendMessage: (sessionId: string, message: string, currentProposal?: Record<string, unknown>) =>
    api.post<{ proposal: Phase1Proposal | Phase2Proposal | null; reply?: string; usage?: TokenUsage }>(
      `/api/agents/wizard/${sessionId}/message`,
      { message, currentProposal }
    ),

  confirmPhase: (sessionId: string, confirmedData: Record<string, unknown>) =>
    api.post<ConfirmResponse>(
      `/api/agents/wizard/${sessionId}/confirm`,
      { confirmedData }
    ),

  complete: (sessionId: string) =>
    api.post<CompleteResponse>(`/api/agents/wizard/${sessionId}/complete`),

  submitManualDescription: (sessionId: string, description: string) =>
    api.post<{ proposal: Phase1Proposal }>(
      `/api/agents/wizard/${sessionId}/manual`,
      { description }
    ),
};
